/**
 * N-05 «Переход из уведомления».
 *
 * BS §13.5 и SCREEN_CATALOG §15: переход ВСЕГДА идёт через
 * `GET /notifications/{notificationId}` → маршрут объекта по закрытому
 * перечню `entity_type`. Перед показом клиент получает актуальное
 * состояние объекта (ПС §20.10) и показывает закрытое/недоступное
 * состояние вместо несуществующего.
 *
 * Неизвестные значения `type`/`entity_type` (правило открытых перечислений
 * BS §16.2) и 404 → N-01, уведомление при этом отмечается прочитанным.
 */
import { useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import type { Notification } from '@shared/api/types'
import { notificationsRepo } from '@shared/db'
import { Spinner } from '@shared/ui'

export function NotificationTargetScreen(): React.ReactElement {
  const { notificationId } = useParams({ strict: false }) as { notificationId: string }
  const navigate = useNavigate()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        const notification = unwrap(
          await api.GET('/notifications/{notificationId}', {
            params: { path: { notificationId } },
          }),
        )

        await notificationsRepo.put(notification)
        if (cancelled) return

        await routeTo(notification, navigate)
      } catch {
        if (!cancelled) setFailed(true)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [notificationId, navigate])

  useEffect(() => {
    if (failed) void navigate({ to: '/notifications', replace: true })
  }, [failed, navigate])

  return (
    <div style={{ display: 'grid', placeItems: 'center', flex: 1, minHeight: 240 }}>
      <Spinner size={28} label="Открываем" />
    </div>
  )
}

/** Закрытый перечень `entity_type` (BS §13.1, GAP-ALL-08). */
async function routeTo(notification: Notification, navigate: ReturnType<typeof useNavigate>): Promise<void> {
  const payload = notification.payload as Record<string, unknown>
  const entityId = notification.entity_id
  const replace = true

  /*
   * Изменение v1.12. Код для входа никуда не ведёт: он показан в самом уведомлении (N-01),
   * и переход в настройки увёл бы от того, ради чего уведомление и пришло.
   */
  if (notification.type === 'security' && payload['event'] === 'login_code') {
    void navigate({ to: '/notifications', replace })
    return
  }

  // Строкой: бэкенд шлёт события безопасности с `entity_type = account` — его в перечне контракта нет.
  switch (notification.entity_type as string) {
    case 'match': {
      // Правило актуального состояния: закрытый match ведёт на M-06, а не в чат.
      if (!entityId) break
      try {
        const match = unwrap(await api.GET('/matches/{matchId}', { params: { path: { matchId: entityId } } }))
        if (match.state === 'closed') break

        if (match.conversation_id) {
          void navigate({
            to: '/chats/$conversationId',
            params: { conversationId: match.conversation_id },
            replace,
          })
          return
        }
      } catch {
        // 404 — совпадение закрыто; уходим в список.
      }
      break
    }

    case 'conversation':
    case 'message': {
      const conversationId = String(
        payload['conversation_id'] ?? (notification.entity_type === 'conversation' ? entityId : ''),
      )
      if (!conversationId) break
      const topicId = payload['topic_id']
      if (typeof topicId === 'string' && topicId) {
        void navigate({
          to: '/chats/$conversationId/topics/$topicId',
          params: { conversationId, topicId },
          search: typeof payload['seq'] === 'number' ? { seq: payload['seq'] } : {},
          replace,
        })
        return
      }
      void navigate({ to: '/chats/$conversationId', params: { conversationId }, replace })
      return
    }

    /*
     * Изменение v1.14: звонок живёт в личном диалоге пары. Если он ещё звонит, экран вызова
     * откроется поверх диалога сам (CallHost), а итог звонка — сообщение в этом же диалоге.
     */
    case 'call': {
      const conversationId = payload['conversation_id']
      if (typeof conversationId !== 'string' || !conversationId) break
      void navigate({ to: '/chats/$conversationId', params: { conversationId }, replace })
      return
    }

    case 'session':
    case 'account':
      void navigate({ to: '/settings/$section', params: { section: 'sessions' }, replace })
      return

    case 'user':
      void navigate({ to: '/settings/$section', params: { section: 'identities' }, replace })
      return

    // §15 каталога: `photo` → фото в редактировании профиля (U-03; до v1.15 — U-04).
    case 'photo':
      void navigate({ to: '/me/edit', replace })
      return

    case 'profile_field':
      void navigate({ to: '/me/edit', replace })
      return

    case 'report':
      if (!entityId) break
      void navigate({ to: '/reports/$reportId', params: { reportId: entityId }, replace })
      return

    case 'appeal':
      if (!entityId) break
      void navigate({ to: '/appeals/$appealId', params: { appealId: entityId }, replace })
      return

    /*
     * `none` встречается только у системных уведомлений; единственный
     * подтип с переходом — `consent_required` → SYS-05, который показывается
     * блокирующим экраном поверх интерфейса, а не маршрутом.
     */
    case 'none':
      break

    case 'sanction':
      void navigate({ to: '/settings/$section', params: { section: 'restrictions' }, replace })
      return

    case 'export':
      void navigate({ to: '/settings/$section', params: { section: 'export' }, replace })
      return

    case 'ticket':
      void navigate({ to: '/settings/$section', params: { section: 'support' }, replace })
      return

    default:
      break
  }

  void navigate({ to: '/notifications', replace })
}
